// src/pages/Overview.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { API_CONFIG } from '../config/apiConfig';
import { DollarSign, TrendingUp, TrendingDown, PiggyBank, PieChart, Banknote, ShieldCheck } from 'lucide-react';

// --- ONLY IMPORT THE NEW SUMMARY FUNCTION ---
import { fetchFinancialSummary, FinancialSummary } from '../services/dashboardService';


// --- NEW, SIMPLIFIED KPI CARD COMPONENT ---
interface KpiCardProps {
  title: string;
  value: string;
  description: string;
  icon: React.ElementType;
  color: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ title, value, description, icon: Icon, color }) => {
  return (
    <div className={`p-6 rounded-2xl shadow-lg border border-gray-200 bg-white flex flex-col h-full`}>
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
        <span className="text-xs font-semibold text-gray-400">{title}</span>
      </div>
      <div className="mt-auto">
        <h2 className="text-4xl font-bold text-gray-800 mt-4">{value}</h2>
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      </div>
    </div>
  );
};


// --- THE MAIN OVERVIEW COMPONENT ---
const Overview: React.FC = () => {
    const { state, dispatch } = useAppContext();
    const { token } = useAuth();
    const navigate = useNavigate();

    const [summaryData, setSummaryData] = useState<FinancialSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Profile check useEffect (Unchanged)
    useEffect(() => {
      const checkProfile = async () => {
        if (!token) return;
        try {
          const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/profile`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (response.status === 404) {
            navigate('/CompleteProfile', { replace: true });
          }
        } catch (error) { console.error("Error checking user profile:", error); }
      };
      checkProfile();
    }, [token, navigate]);

    // Formatting functions (Unchanged)
    const formatCurrency = useCallback((value: number | undefined): string => {
        const num = Number(value);
        const symbol = state.currencySymbol || '₹'; 
        if (isNaN(num)) return `${symbol}0`;
        return `${symbol}${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(num)}`;
    }, [state.currencySymbol]);

    useEffect(() => {
        const storedSymbol = localStorage.getItem('currencySymbol');
        if (storedSymbol && storedSymbol !== state.currencySymbol) {
            dispatch({ type: 'SET_CURRENCY_SYMBOL', payload: storedSymbol });
        }
    }, [dispatch, state.currencySymbol]);

    // --- REWRITTEN DATA FETCHING LOGIC ---
    useEffect(() => {
      const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const summaryRes = await fetchFinancialSummary();
            setSummaryData(summaryRes);
        } catch (err: any) { 
            console.error("Failed to fetch dashboard summary data:", err);
            setError(err.message || "Could not load data.");
        } finally { 
            setLoading(false); 
        }
      };
      
      if (token) {
        fetchData();
      }
    }, [token]);

    if (loading) {
        return <div className="p-6 text-center text-gray-500">Loading Financial Overview...</div>;
    }

    if (error) {
        return <div className="p-6 text-center text-red-500">Error: {error}</div>;
    }

    return (
      <div className="bg-gray-50 min-h-screen p-6 font-sans">
        <header className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Financial Dashboard</h1>
        </header>

        {/* This is the main grid for your new KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          
          {/* --- ROW 1: CORE METRICS --- */}
          
          {/* 1. Net Worth */}
          <KpiCard
            title="Net Worth"
            value={formatCurrency(summaryData?.netWorth.netWorth)}
            description="Total Assets - Total Liabilities"
            icon={DollarSign}
            color="bg-green-500"
          />

          {/* 2. Net Savings */}
          <KpiCard
            title="Net Annual Savings"
            value={formatCurrency(summaryData?.savingsAndCashFlow.netAnnualSavings)}
            description="Annual Income - Annual Expenses"
            icon={PiggyBank}
            color="bg-blue-500"
          />

          {/* 3. Savings Ratio */}
          <KpiCard
            title="Savings Ratio"
            value={`${((summaryData?.savingsAndCashFlow.savingRatio || 0) * 100).toFixed(1)}%`}
            description="Net Savings / Total Income"
            icon={PieChart}
            color="bg-sky-500"
          />
          
          {/* --- ROW 2: DEBT RATIOS --- */}
          
          {/* 4. Loan to Asset Ratio */}
          <KpiCard
            title="Loan to Asset Ratio"
            value={`${((summaryData?.debtRatios.loanToAssetRatio || 0) * 100).toFixed(1)}%`}
            description="How much of your assets are financed by debt"
            icon={TrendingDown}
            color="bg-orange-500"
          />

          {/* 5. Debt Service Ratio (DSR) */}
          <KpiCard
            title="Debt Service Ratio"
            value={`${((summaryData?.debtRatios.debtServiceRatio || 0) * 100).toFixed(1)}%`}
            description="Monthly debt payments vs. gross income"
            icon={Banknote}
            color="bg-red-500"
          />

          {/* --- ROW 3: PLACEHOLDERS FOR FUTURE FUNCTIONALITY --- */}
          
          {/* 6. Post-Tax Income (Placeholder) */}
          <KpiCard
            title="Post-Tax Income"
            value="Calculate"
            description="Requires an effective tax rate"
            icon={TrendingUp}
            color="bg-gray-400"
          />

          {/* 7. Goal Readiness (Placeholder) */}
          <KpiCard
            title="Goal Readiness Score"
            value="Calculate"
            description="Compare investments to an inflation-adjusted goal"
            icon={ShieldCheck}
            color="bg-gray-400"
          />

        </div>
      </div>
    );
};
  
export default Overview;